import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OrderListItem } from './useOrders';
import { apiFetch } from '../lib/api';

interface ReorderPriorityRequest {
  orderId: string;
  afterOrderId: string | null;
  beforeOrderId: string | null;
}

interface ReorderPriorityResponse {
  data: {
    orderId: string;
    newPriority: string;
    updated: boolean;
  };
}

async function updatePriority(request: ReorderPriorityRequest): Promise<ReorderPriorityResponse> {
  return apiFetch<ReorderPriorityResponse>(`/api/v1/orders/${request.orderId}/priority`, {
    method: 'PUT',
    body: JSON.stringify({
      afterOrderId: request.afterOrderId,
      beforeOrderId: request.beforeOrderId,
    }),
  });
}

interface UsePriorityUpdateOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function usePriorityUpdate(options: UsePriorityUpdateOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePriority,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['orders'] });
      await queryClient.cancelQueries({ queryKey: ['priority-list'] });

      // Snapshot previous value for rollback
      const previousOrders = queryClient.getQueryData(['priority-list']);

      return { previousOrders };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousOrders) {
        queryClient.setQueryData(['priority-list'], context.previousOrders);
      }
      options.onError?.(error as Error);
    },
    onSuccess: () => {
      options.onSuccess?.();
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['priority-list'] });
    },
  });
}

/**
 * Helper to reorder an array optimistically.
 * Returns a new array with the item at oldIndex moved to newIndex.
 */
export function reorderArray<T>(array: T[], oldIndex: number, newIndex: number): T[] {
  const result = [...array];
  const removed = result.splice(oldIndex, 1)[0];
  if (removed !== undefined) {
    result.splice(newIndex, 0, removed);
  }
  return result;
}

/**
 * Calculate afterOrderId and beforeOrderId for a priority update.
 * @param orders - Current list of orders in priority order
 * @param oldIndex - Original position of the moved order
 * @param newIndex - New position of the moved order
 */
export function calculatePriorityUpdate(
  orders: OrderListItem[],
  oldIndex: number,
  newIndex: number
): { afterOrderId: string | null; beforeOrderId: string | null } {
  // Reorder to get the new array
  const reordered = reorderArray(orders, oldIndex, newIndex);

  // Get the order that was moved
  const movedOrder = reordered[newIndex];

  // Get neighbors in the new position
  const afterOrder = newIndex > 0 ? reordered[newIndex - 1] : null;
  const beforeOrder = newIndex < reordered.length - 1 ? reordered[newIndex + 1] : null;

  return {
    afterOrderId: afterOrder?.id ?? null,
    beforeOrderId: beforeOrder?.id ?? null,
  };
}
